import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik } from 'formik'
import * as Yup from 'yup'

import { CompanyAddressValues } from 'core/application/adminSignUp/common/models'
import statesApi, { StateValues } from 'core/application/commons/states'

import { StyledInput as Input, InputMask, SelectField as Select } from 'common/components/dataEntry/formik'
import CategoryItemPageHeader from 'common/components/onBoarding/Common'
import { formatOnlyChars, zipNumberRegex } from 'common/components/utils/InputUtils'
import { filterByKeyAndValue } from 'common/components/utils/SelectFilterUtils'

import { Alert, Col, Option, Row } from 'common/components'
import { BaseNavigationProps } from 'common/wizard/BaseNavigationProps'

import useCommandRequest from '../../common/hooks/useCommandRequest'
import WizardNextButton from './WizardNextButton'
import WizardPreviousButton from './WizardPreviousButton'

import './CompanyDetailsStyles.less'

interface CompanyAddressPros extends BaseNavigationProps<CompanyAddressValues> {
  onSubmitFormHandler: (values: CompanyAddressValues) => Promise<any>
  message: string
  description: string
  currentQuestion?: number
  numberOfQuestions?: number
}

const CompanyAddress = (props: CompanyAddressPros) => {
  const [statesList, setStatesList] = useState<StateValues[]>()
  const { t: translate } = useTranslation()
  const [sendCommand, isLoading] = useCommandRequest()

  useEffect(() => {
    statesApi().then((response) => {
      setStatesList(response)
    })
  }, [])

  const onSubmit = async (values: CompanyAddressValues) => {
    const result = await sendCommand(() => props.onSubmitFormHandler(values))

    if (result.completed) {
      props.goNext(props.name)
    }
  }

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate)}
      initialValues={props.state}
      onSubmit={onSubmit}
      validate={(values) => {
        props.hasDirtyData(true)
      }}
    >
      {(formik) => (
        <form
          onSubmit={formik.handleSubmit}
          autoComplete="off"
          onBlur={formik.handleBlur}
          className={'t-admin-signup-company-address'}
        >
          <Row>
            <Col xs={{ span: 22, offset: 1 }} md={{ span: 16, offset: 3 }} xl={{ span: 8 }}>
              <CategoryItemPageHeader
                title={translate('admin-signup.company-details.title')}
                numberOfQuestions={props.numberOfQuestions ?? 5}
                currentQuestion={props.currentQuestion ?? 3}
                question={translate('admin-signup.company-details.company-address.question')}
              />
            </Col>
          </Row>

          <Row className="form__row mt-16">
            <Col xs={{ span: 22, offset: 1 }} md={{ span: 16, offset: 3 }} xl={{ span: 12 }}>
              <div className="pl-12 mb-8">
                <Alert
                  type="info"
                  className={'t-admin-signup-company-address-info'}
                  showIcon
                  message={props.message}
                  description={props.description}
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col xs={{ span: 22, offset: 1 }} md={{ span: 16, offset: 3 }} xl={{ span: 12 }}>
              <Row className="form__row mt-32" gutter={24}>
                <Col xs={24} md={12}>
                  <Input
                    label={translate('admin-signup.company-details.company-address.address-line-1-label')}
                    name="addressLine1"
                  />
                </Col>
                <Col xs={24} md={12}>
                  <Input
                    label={translate('admin-signup.company-details.company-address.address-line-2-label')}
                    name="addressLine2"
                    hint={`(${translate('general.optional')})`}
                  />
                </Col>
              </Row>
              <Row className="form__row mt-24" gutter={24}>
                <Col xs={24} md={12}>
                  <Input label={translate('admin-signup.company-details.company-address.city-label')} name="city" />
                </Col>
                <Col xs={24} md={12}>
                  <InputMask
                    label={translate('admin-signup.company-details.company-address.zip-label')}
                    name="zipCode"
                    mask="00000"
                    placeholder=""
                    className={'formik_input'}
                  />
                </Col>
              </Row>
              <Row className="form__row mt-24" gutter={24}>
                <Col xs={24} md={12}>
                  <Select
                    name="stateId"
                    label={translate('admin-signup.company-details.company-address.state-label')}
                    placeholder={translate('admin-signup.company-details.company-address.state-placeholder')}
                    filterOption={filterByKeyAndValue}
                    className="state__select"
                    defaultValue={props.state.stateId ? [props.state.stateId] : undefined}
                  >
                    {statesList?.map((state) => (
                      <Option key={state.id} value={state.name}>
                        {state.id}
                      </Option>
                    ))}
                  </Select>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className="bottom__actions">
            <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 3 }}>
              <WizardPreviousButton
                onClick={() => {
                  props.goBack(props.name)
                }}
              />
              <WizardNextButton disabled={!formValidator(translate).isValidSync(formik.values)} loading={isLoading} />
            </Col>
          </Row>
        </form>
      )}
    </Formik>
  )
}

export default CompanyAddress

const formValidator = (translate: any) =>
  Yup.object({
    addressLine1: Yup.string().trim().required(translate('general.required')),

    addressLine2: Yup.string()
      .nullable()
      .notRequired()
      .trim()
      .transform((value) => (value && value.length > 0 ? value : null)),
    city: Yup.string()
      .trim()
      .required(translate('general.required'))
      .matches(formatOnlyChars, translate('admin-signup.company-details.errors.company-name-regex')),
    zipCode: Yup.string()
      .trim()
      .required(translate('general.required'))
      .matches(zipNumberRegex, translate('admin-signup.company-details.errors.zip-number-regex')),
    stateId: Yup.string().required(translate('general.required')),
  })
